/* eslint-disable */
import React, { createContext, useState, useContext } from 'react';
import { hydrateMyVWSession, persistMyVWSession } from '../src/services/myVWSessionPersistence';

interface SessionInput {
  token: string;
  brand: string;
  country: string;
}

interface MyVWSessionFields {
  token: string;
  accountNumber: string;
  brand: string;
  country: string;
  language: string;
  externalId: string;
  correlationId: string;
  vin: string;
  email: string;
}

interface MyVWContextType extends MyVWSessionFields {
  isLinked: boolean | null;
  vwfsToken: string;
  partyId: number | null;
  ssn: string;
  loginAuthenticatorCode: string;
  username: string;
  applyAuthState: (state: {
    partyId?: number | null;
    vwfsToken?: string;
    email?: string;
    ssn?: string;
    phoneNumber?: string;
    accountNumber?: string;
    brand?: string;
    country?: string;
    externalId?: string;
    loginAuthenticatorCode?: string;
    username?: string;
  }) => void;
  setIsLinked: (v: boolean) => void;
  init: (params: SessionInput) => void;
}

type MyVWSessionState = MyVWSessionFields & {
  isLinked: boolean | null;
  vwfsToken: string;
  partyId: number | null;
  ssn: string;
  phoneNumber: string;
  loginAuthenticatorCode: string;
  username: string;
};

const defaultContext: MyVWContextType = {
  token: '',
  externalId: '',
  accountNumber: '',
  brand: '',
  country: '',
  language: 'en-US',
  vin: '',
  email: '',
  correlationId: '',
  isLinked: null,
  vwfsToken: '',
  partyId: null,
  ssn: '',
  loginAuthenticatorCode: '',
  username: '',
  applyAuthState: () => { },
  setIsLinked: () => { },
  init: () => { },
};

export const MyVWContext = createContext<MyVWContextType>(defaultContext);

const getInitialSessionState = (): MyVWSessionState => {
  const hydrated = hydrateMyVWSession();
  return {
    token: hydrated?.token ?? '',
    accountNumber: hydrated?.accountNumber ?? '',
    brand: hydrated?.brand ?? '',
    country: hydrated?.country ?? '',
    language: 'en-US',
    externalId: '',
    partyId: hydrated?.partyId ?? null,
    phoneNumber: '',
    correlationId: '',
    vin: '',
    email: hydrated?.email ?? '',
    isLinked: hydrated?.isLinked ?? null,
    vwfsToken: hydrated?.vwfsToken ?? '',
    ssn: '',
    loginAuthenticatorCode: hydrated?.loginAuthenticatorCode ?? '',
    username: hydrated?.username ?? '',
  };
};

export const MyVWContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sessionState, setSessionState] = useState<MyVWSessionState>(getInitialSessionState);
  const updateSession = (updater: (prev: MyVWSessionState) => MyVWSessionState) => {
    setSessionState((prev) => {
      const next = updater(prev);
      persistMyVWSession(next);
      return next;
    });
  };

  const applyAuthState = (state: {
    partyId?: number | null;
    vwfsToken?: string;
    email?: string;
    ssn?: string;
    phoneNumber?: string;
    accountNumber?: string;
    brand?: string;
    country?: string;
    externalId?: string;
    loginAuthenticatorCode?: string;
    username?: string;
  }) => {
    updateSession((prev) => ({
      ...prev,
      ...(state.partyId !== undefined ? { partyId: state.partyId } : {}),
      ...(state.vwfsToken !== undefined ? { vwfsToken: state.vwfsToken } : {}),
      ...(state.email !== undefined ? { email: state.email } : {}),
      ...(state.ssn !== undefined ? { ssn: state.ssn } : {}),
      ...(state.phoneNumber !== undefined ? { phoneNumber: state.phoneNumber } : {}),
      ...(state.accountNumber !== undefined ? { accountNumber: state.accountNumber } : {}),
      ...(state.brand !== undefined ? { brand: state.brand } : {}),
      ...(state.country !== undefined ? { country: state.country } : {}),
      ...(state.externalId !== undefined ? { externalId: state.externalId } : {}),
      ...(state.loginAuthenticatorCode !== undefined ? { loginAuthenticatorCode: state.loginAuthenticatorCode } : {}),
      ...(state.username !== undefined ? { username: state.username } : {}),
    }));
  };

  const setIsLinked = (v: boolean) => updateSession(prev => ({ ...prev, isLinked: v }));
  const init = (p: SessionInput) => {
    updateSession(prev => {
      if (prev.token === p.token && prev.brand === p.brand && prev.country === p.country) {
        return prev;
      }
      return { ...prev, token: p.token, brand: p.brand, country: p.country };
    });
  };

  return (
    <MyVWContext.Provider
      value={{
        ...sessionState,
        applyAuthState,
        setIsLinked,
        init,
      }}
    >
      {children}
    </MyVWContext.Provider>
  );
};

export const useMyVW = (): MyVWContextType => useContext(MyVWContext);
